import React, { useContext, useEffect, useState } from 'react';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import RemoteAccessDashboardContext from '@totem/components/services/remoteAccess/v2/summary/remoteAccessDashboardContext';
import {
  RemoteAccessDashboard,
  RemoteAccessOrganizationRiskSummaries,
} from '@totem/components/services/remoteAccess/v2/summary/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { REMOTE_ACCESS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: React.ReactNode;
};

const RemoteAccessDashboardContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const organizationId = userProfile.organization?.id;
  const [refreshDashboard, setRefreshDashboard] = useState<boolean>(true);
  const [refreshOrganizationSummary, setRefreshOrganizationSummary] =
    useState<boolean>(false);
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(true);
  const [organizationSummaryLoading, setOrganizationSummaryLoading] =
    useState<boolean>(true);
  const [dashboardData, setDashboardData] =
    useState<RemoteAccessDashboard>(null);
  const [organizationSummaryData, setOrganizationSummaryData] =
    useState<RemoteAccessOrganizationRiskSummaries>(null);
  const [selectedBuildingId, setSelectedBuildingId] =
    useState<string>(EMPTY_ID);

  useEffect(() => {
    setSelectedBuildingId(EMPTY_ID);

    setRefreshDashboard(true);
  }, [organizationId]);

  useEffect(() => {
    if (!refreshDashboard) {
      return;
    }
    setRefreshDashboard(false);

    fetch(`${REMOTE_ACCESS_ENDPOINT}/dashboard/${selectedBuildingId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((result: RemoteAccessDashboard) => {
        setDashboardData(result?.userSummary ? result : null);
      })
      .finally(() => {
        setDashboardLoading(false);

        setRefreshOrganizationSummary(true);
      });
  }, [getApiToken, refreshDashboard]);

  useEffect(() => {
    if (!refreshOrganizationSummary) {
      return;
    }
    setRefreshOrganizationSummary(false);

    fetch(
      `${REMOTE_ACCESS_ENDPOINT}/organizationSummary/${selectedBuildingId}`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: RemoteAccessOrganizationRiskSummaries) => {
        setOrganizationSummaryData(result);
      })
      .finally(() => {
        setOrganizationSummaryLoading(false);
      });
  }, [getApiToken, refreshOrganizationSummary]);

  const handleBuildingChange = (newBuildingId: string) => {
    setSelectedBuildingId(newBuildingId);

    setRefreshDashboard(true);
  };

  return (
    <RemoteAccessDashboardContext.Provider
      value={{
        dashboardLoading,
        organizationSummaryLoading,
        dashboardData,
        organizationSummaryData,
        onBuildingChange: handleBuildingChange,
        selectedPropertyId: selectedBuildingId,
      }}
    >
      {children}
    </RemoteAccessDashboardContext.Provider>
  );
};

export default RemoteAccessDashboardContainer;
